/// <amd-module name="InstantSearch/Shared/InstantSearchProviders/QuickNavigationHelpProvider" />
import * as React from "react";
import { IReadonlyObservableArray } from "VSS/Core/Observable";
import { IVssPageContext, Services } from "VSS/Platform/Context";
import { IMenuItem, IMenuItemDetails, MenuCell, MenuItemType } from "VSSUI/Menu";
import { BaseInstantSearchProvider } from "InstantSearch/Shared/InstantSearchProviders/BaseInstantSearchProvider";
import { ResultRow } from "InstantSearch/Shared/ResultRow";
import { IInstantSearchMetadataService } from "InstantSearch/Shared/Services/InstantSearchMetadataService";
import { TelemetryWriter } from "InstantSearch/Shared/TelemetryWriter";
import { IInstantSearchMenuService, IMenuServiceContext } from "InstantSearch/Shared/Types";
import { IInvokedProvidersManager } from "InstantSearch/Shared/Services/InvokedProvidersManager";

/**
 * Service for displaying quick navigation help on typing '/' in search box.
 */
export class QuickNavigationHelpProvider extends BaseInstantSearchProvider<undefined> implements IInstantSearchMenuService<IMenuServiceContext> {
    protected _contributionId = "ms.vss-work-web.quick-navigation-help-menu-provider";

    public _serviceStart(pageContext: IVssPageContext): void {
        super._serviceStart(pageContext);
        this._initContribution(this._contributionId);
    }

    protected _getMenuItems(menuId: string,
        context: IMenuServiceContext,
        invokeProvider: boolean,
        searchText: string
    ): IReadonlyObservableArray<IMenuItem> | null {
        const { searchInput, invokedProvidersProps } = context;
        const queryText = searchInput.queryText().trim().toLowerCase();
        const isProviderInvoked = invokedProvidersProps && invokedProvidersProps.key && invokedProvidersProps.key !== "/" ? true : false;
        if (queryText.startsWith("/") && !isProviderInvoked) {
            this._loadQuickNavigationHelpMenuItems(context, queryText);
        } else if (this._menuItems.length > 0) {
            this._menuItems.splice(0, this._menuItems.length);
        }

        return this._menuItems;
    }

    private _loadQuickNavigationHelpMenuItems(context: IMenuServiceContext, queryText: string): void {
        const metadataService = this.pageContext.getService<IInstantSearchMetadataService>("IInstantSearchMetadataService");
        const helpProps = metadataService.providerInvocationHelpProps.filter((props) => props.key.startsWith(queryText));
        const { invokedProvidersProps } = context
        const header = invokedProvidersProps && invokedProvidersProps.props ? invokedProvidersProps.props.header : "";
        this._menuItems.splice(0, this._menuItems.length);
        if (helpProps.length > 0) {
            // Add header
            this._menuItems.push({
                id: "",
                groupKey: this._groupKey,
                rank: this._rank,
                itemType: MenuItemType.Header,
                text: header,
                className: "search-suggestion-header"
            });

            // Add quick navigation menu items
            helpProps.forEach((props, index) => {
                const { searchInput, telemetryWriter } = context;
                const { iconName } = props;
                const { title, position } = props.quickNavigationHelpProps!;
                this._menuItems.push({
                    id: "",
                    groupKey: this._groupKey,
                    rank: this._rank + position,
                    renderMenuCell: (menuCell: MenuCell, menuItem: IMenuItem, details: IMenuItemDetails): JSX.Element | undefined => {
                        switch (menuCell) {
                            case MenuCell.PrimaryText:
                                return <ResultRow iconProps={{ iconName: iconName || "ArrowUpRight8" }} title={title} />;
                            default:
                                return undefined;
                        }
                    },
                    onActivate: (menuItem: IMenuItem, event: React.KeyboardEvent<HTMLElement>) => {
                        const invokedProvidersManager = this.pageContext.getService<IInvokedProvidersManager>("IInvokedProvidersManager");
                        invokedProvidersManager.setInvokedProvider(props.key, searchInput, telemetryWriter);
                        this._recordQuickNavigationHelpClickTelemetry(index, telemetryWriter);
                    }
                });
            });
        }
    }

    private _recordQuickNavigationHelpClickTelemetry = (index: number, telemetryWriter: TelemetryWriter): void => {
        telemetryWriter.publish("ResultClicked", {
            index: index,
            resultType: "QuickNavigationHelp"
        });
    };
}

Services.add("quick-navigation-help-provider-service", { serviceFactory: QuickNavigationHelpProvider });
